import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logoWhiteSvg from "../../../branding/logo_white.svg";

interface FullscreenLoaderProps {
  minDurationMs?: number;
  onFinished?: () => void;
}

export function FullscreenLoader({
  minDurationMs = 800,
  onFinished,
}: FullscreenLoaderProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      onFinished?.();
    }, minDurationMs);

    return () => clearTimeout(timer);
  }, [minDurationMs, onFinished]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="status"
          aria-label="Loading RADIECE..."
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            scale: 1.03,
            transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
          }}
          className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-[#0d1723] overflow-hidden"
        >
          {/* Background Ambient Radial Glow */}
          <div
            className="absolute w-[500px] h-[500px] bg-primary/15 rounded-full blur-[100px] pointer-events-none ambient-glow"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage:
                "radial-gradient(circle at 50% 50%, #39b7e9 0%, transparent 60%)",
            }}
          />

          {/* Center Logo Container */}
          <div className="relative flex flex-col items-center z-10 px-6 max-w-sm w-full text-center">
            {/* Glowing Logo Frame */}
            <div className="relative mb-6">
              <div
                className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-brand-light/30 rounded-2xl blur-xl opacity-60 animate-pulse"
                aria-hidden="true"
              />
              <div className="relative">
                <img
                  src={logoWhiteSvg}
                  alt="RADIECE"
                  width={260}
                  height={65}
                  className="w-56 sm:w-64 h-auto object-contain drop-shadow-[0_0_25px_rgba(57,183,233,0.5)] animate-float-soft"
                />
              </div>
            </div>

            {/* Brand Tagline */}
            <p className="text-xs sm:text-sm font-nexa uppercase tracking-[0.25em] text-slate-300 mb-8 opacity-90">
              Intelligence, Engineered.
            </p>

            {/* High-Tech Glowing Progress Bar */}
            <div className="w-48 h-1 bg-slate-800/80 rounded-full overflow-hidden relative shadow-inner border border-white/10">
              <div className="absolute inset-y-0 bg-gradient-to-r from-brand via-primary to-sky-300 w-1/2 rounded-full animate-[loaderBeam_1.4s_cubic-bezier(0.65,0,0.35,1)_infinite]" />
            </div>

            {/* Location & Status Line */}
            <span className="mt-5 text-[11px] font-mono tracking-widest text-slate-500 uppercase">
              CSEZ • Kakkanad • Kerala
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
